import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layers3, RefreshCw, Save, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../services/api';
import {
  DEFAULT_PROFIT_SHARING_CONFIG,
  getProfitSharingRatioTotals,
  isProfitSharingConfigDirty,
  normalizeProfitSharingDefaults,
  validateProfitSharingDefaults
} from '../utils/profitSharingDefaults';

export default function ProfitRuleConfig() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(DEFAULT_PROFIT_SHARING_CONFIG);
  const [savedConfig, setSavedConfig] = useState(DEFAULT_PROFIT_SHARING_CONFIG);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await settingsAPI.getProfitSharingDefaults();
      if (!response.data?.success || !response.data?.data) throw new Error('Invalid profit-sharing response');
      const next = normalizeProfitSharingDefaults(response.data.data);
      setConfig(next);
      setSavedConfig(next);
      setMeta(response.data.meta || null);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.message || t('profitRulesPage.errors.load') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const totals = useMemo(() => getProfitSharingRatioTotals(config), [config]);
  const dirty = isProfitSharingConfigDirty(config, savedConfig);

  const saveConfig = async () => {
    const validation = validateProfitSharingDefaults(config);
    if (!validation.valid) {
      setMessage({ type: 'error', text: t(`systemSettings.profitValidation.${validation.code}`) });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const response = await settingsAPI.updateProfitSharingDefaults(validation.config);
      if (!response.data?.success || !response.data?.data) throw new Error('Invalid save response');
      const next = normalizeProfitSharingDefaults(response.data.data);
      setConfig(next);
      setSavedConfig(next);
      setMeta(response.data.meta || null);
      setMessage({ type: 'success', text: t('profitRulesPage.success') });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || error.message || t('profitRulesPage.errors.save') });
    } finally {
      setSaving(false);
    }
  };

  const setNumber = (field, value) => setConfig((previous) => ({
    ...previous,
    [field]: value === '' ? '' : Number(value)
  }));

  const numberField = (field, label) => (
    <div key={field} className="min-w-0">
      <label className="mb-2 block text-[11px] font-black uppercase text-[var(--admin-text-muted)]">{label}</label>
      <input
        data-testid={`profit-field-${field}`}
        type="number"
        value={config[field]}
        onChange={(event) => setNumber(field, event.target.value)}
        className="w-full rounded-lg border border-[var(--admin-border)] bg-[var(--admin-bg-card)] px-4 py-3 text-sm font-semibold text-[var(--admin-text-primary)] outline-none focus:border-[var(--admin-accent)]"
      />
    </div>
  );

  const tiers = [
    {
      id: 'tier1', total: 100, valid: true,
      fields: [['tier1Threshold', t('profitRulesPage.fields.tier1Threshold')]]
    },
    {
      id: 'tier2', total: totals.tier2, valid: totals.tier2 === 100,
      fields: [
        ['tier2Threshold', t('profitRulesPage.fields.tier2Threshold')],
        ['tier2StewardRatio', t('profitRulesPage.fields.tier2Steward')],
        ['tier2RpRatio', t('profitRulesPage.fields.tier2Rp')],
        ['tier2PuskudRatio', t('profitRulesPage.fields.tier2Puskud')]
      ]
    },
    {
      id: 'tier3', total: totals.tier3, valid: totals.tier3 === 50,
      fields: [
        ['tier3Threshold', t('profitRulesPage.fields.tier3Threshold')],
        ['tier3StewardRatio', t('profitRulesPage.fields.tier3Steward')],
        ['tier3RpRatio', t('profitRulesPage.fields.tier3Rp')],
        ['tier3PuskudRatio', t('profitRulesPage.fields.tier3Puskud')]
      ]
    },
    {
      id: 'tier4', total: totals.tier4, valid: totals.tier4 === 50,
      fields: [
        ['tier4StewardRatio', t('profitRulesPage.fields.tier4Steward')],
        ['tier4RpRatio', t('profitRulesPage.fields.tier4Rp')],
        ['tier4PuskudRatio', t('profitRulesPage.fields.tier4Puskud')]
      ]
    }
  ];

  return (
    <div className="space-y-6 p-4 text-[var(--admin-text-primary)] sm:p-6 lg:p-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <h2 className="flex items-center gap-3 text-2xl font-black sm:text-3xl">
            <TrendingUp className="shrink-0 text-[var(--admin-accent)]" size={30} />
            {t('profitRulesPage.title')}
          </h2>
          <p className="mt-2 max-w-4xl text-sm leading-6 text-[var(--admin-text-muted)]">{t('profitRulesPage.subtitle')}</p>
          {meta?.version ? <p className="mt-2 text-xs font-bold text-[var(--admin-text-muted)]">{t('systemSettings.profitConfigVersion', { version: meta.version })}</p> : null}
        </div>
        <button data-testid="refresh-profit-rules" type="button" onClick={loadConfig} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--admin-border)] bg-[var(--admin-bg-card)] px-4 py-3 text-sm font-black">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> {t('common.refresh')}
        </button>
      </header>

      {message ? <div role={message.type === 'error' ? 'alert' : 'status'} className="rounded-lg border border-[var(--admin-accent)] bg-[var(--admin-surface-soft)] px-4 py-3 text-sm font-bold">{message.text}</div> : null}

      <section className="admin-card rounded-lg p-4 sm:p-5" data-testid="profit-rules-global-panel">
        <div className="mb-4 flex items-center gap-2 text-lg font-black"><Layers3 size={19} className="text-[var(--admin-accent)]" />{t('profitRulesPage.tiers.title')}</div>
        <p className="mb-4 rounded-lg border border-[var(--admin-border)] bg-[var(--admin-surface-soft)] p-3 text-xs font-semibold leading-5 text-[var(--admin-text-muted)]">{t('profitRulesPage.hints.thresholds')}</p>
        <div className="space-y-4" data-testid="profit-tier-list">
          {tiers.map((tier) => <article key={tier.id} className="admin-muted-panel grid gap-4 rounded-xl p-4 xl:grid-cols-[minmax(220px,0.75fr)_minmax(0,2fr)]">
            <div>
              <div className="font-black">{t(`profitRulesPage.tiers.${tier.id}Title`)}</div>
              <div className="mt-1 text-xs leading-5 text-[var(--admin-text-muted)]">{t(`profitRulesPage.tiers.${tier.id}Desc`)}</div>
              <span className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-black ${tier.valid ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{tier.id === 'tier1' ? t('profitRulesPage.hints.tier1') : `${tier.total}%`}</span>
            </div>
            <div className={`grid gap-3 ${tier.fields.length === 1 ? 'sm:grid-cols-2 xl:grid-cols-1' : 'sm:grid-cols-2 xl:grid-cols-4'}`}>{tier.fields.map(([field, label]) => numberField(field, label))}</div>
          </article>)}
        </div>
        <button data-testid="save-profit-rules" type="button" onClick={saveConfig} disabled={!dirty || saving || loading} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--admin-accent)] px-5 py-4 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto">
          <Save size={16} /> {saving ? t('profitRulesPage.saving') : t('profitRulesPage.save')}
        </button>
      </section>
    </div>
  );
}
